import React, { useState, useEffect } from 'react';
import './TVDashboard.css';

const TVDashboard = () => {
  const [queues, setQueues] = useState([]);
  const [theme, setTheme] = useState('dark'); // 'dark' or 'light'
  const [currentTime, setCurrentTime] = useState(new Date());

  const QUEUE_NAMES = [
    'th-support-spp',
    'comfy-sale',
    'comfy-beznal',
    'th-support-1st-line',
    'th-support-1c'
  ];

  const QUEUE_DISPLAY_NAMES = {
    'th-support-spp': 'TH Support SPP',
    'comfy-sale': 'Comfy Sale',
    'comfy-beznal': 'Comfy Beznal',
    'th-support-1st-line': 'TH Support 1st Line',
    'th-support-1c': 'TH Support 1C'
  };

  useEffect(() => {
    fetchQueueStats();
    const interval = setInterval(fetchQueueStats, 3000); // Update every 3 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchQueueStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/queue-stats-public`);
      if (response.ok) {
        const data = await response.json();
        setQueues(data.queues || []);
      }
    } catch (error) {
      console.error('Failed to fetch queue stats:', error);
    }
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const getQueueData = (queueName) => {
    return queues.find(q => q.queue === queueName) || {
      queue: queueName,
      available: 0,
      busy: 0,
      waiting: 0,
      longestWait: 0,
      members: [],
      callers: []
    };
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatPhoneNumber = (number) => {
    if (!number) return 'Unknown';
    // Format Ukrainian numbers: +380XXXXXXXXX -> +380 XX XXX XX XX
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.startsWith('380') && cleaned.length === 12) {
      return `+380 ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 10)} ${cleaned.slice(10)}`;
    }
    return number;
  };

  return (
    <div className={`tv-dashboard ${theme}`}>
      <div className="tv-header">
        <h1>📞 SPP Dashboard</h1>
        <div className="tv-header-right">
          <div className="tv-time">
            {currentTime.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      <div className="tv-grid">
        {QUEUE_NAMES.map(queueName => {
          const queue = getQueueData(queueName);
          const hasWaiting = queue.waiting > 0;
          const allBusy = queue.available === 0 && queue.busy > 0;

          return (
            <div 
              key={queueName} 
              className={`tv-queue-card ${hasWaiting ? 'has-waiting' : ''} ${allBusy ? 'all-busy' : ''}`}
            >
              <div className="tv-queue-header">
                <h2>{QUEUE_DISPLAY_NAMES[queueName]}</h2>
                <div className="tv-queue-summary">
                  <span className="summary-item available">✓ {queue.available}</span>
                  <span className="summary-item busy">📞 {queue.busy}</span>
                  <span className={`summary-item waiting ${hasWaiting ? 'alert' : ''}`}>
                    ⏳ {queue.waiting}
                  </span>
                </div>
              </div>

              <div className="tv-queue-body">
                {/* Operators List */}
                <div className="tv-section">
                  <h3 className="tv-section-title">Operators</h3>
                  <div className="tv-operators-list">
                    {queue.members && queue.members.length > 0 ? (
                      queue.members.map((member, idx) => (
                        <div 
                          key={idx} 
                          className={`tv-operator ${member.status === 'busy' || member.inCall ? 'busy' : 'available'} ${member.paused ? 'paused' : ''}`}
                        >
                          <div className="operator-status-dot"></div>
                          <div className="operator-name">{member.name || `Agent ${idx + 1}`}</div>
                        </div>
                      ))
                    ) : (
                      <div className="tv-empty">No operators</div>
                    )}
                  </div>
                </div>

                {/* Waiting Callers List */}
                <div className="tv-section">
                  <h3 className="tv-section-title">
                    Waiting Callers {hasWaiting && <span className="caller-count">({queue.waiting})</span>}
                  </h3>
                  <div className="tv-callers-list">
                    {queue.callers && queue.callers.length > 0 ? (
                      queue.callers.map((caller, idx) => (
                        <div key={idx} className="tv-caller">
                          <div className="caller-position">#{caller.position || idx + 1}</div>
                          <div className="caller-number">{formatPhoneNumber(caller.callerIdNum)}</div>
                          <div className={`caller-wait ${caller.wait > 60 ? 'long-wait' : ''}`}>
                            {formatTime(caller.wait)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="tv-empty">No waiting callers</div>
                    )}
                  </div>
                </div>
              </div>

              {hasWaiting && queue.longestWait > 60 && (
                <div className="tv-alert-banner">
                  ⚠️ Longest wait: {formatTime(queue.longestWait)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="tv-footer">
        <div className="tv-footer-text">
          🔄 Auto-refresh every 3 seconds • {currentTime.toLocaleDateString('uk-UA')}
        </div>
      </div>
    </div>
  );
};

export default TVDashboard;
